import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Stack, Typography, Alert,
} from '@mui/material';
import axios from 'axios';

// Vendor-rule treatments — must mirror the Enum8 in vat_vendor_rules.treatment.
// Backend endpoint: POST /api/accounting/vat-preview/vendor-rules (vendor, treatment, notes).
const TREATMENTS = [
  { value: 'uk_standard_20', label: 'Standard 20% — UK VAT, reclaimable' },
  { value: 'uk_reduced_5',   label: 'Reduced 5% — UK VAT, reclaimable' },
  { value: 'uk_zero_rated',  label: 'Zero-rated — taxable but 0% rate' },
  { value: 'uk_exempt',      label: 'Exempt — outside UK VAT system' },
  { value: 'reverse_charge', label: 'Reverse charge — self-account both sides' },
  { value: 'outside_scope',  label: 'Outside scope — non-UK supply' },
  { value: 'unknown',        label: 'Unknown' },
];

/**
 * Edits the VAT treatment for a vendor rule. Affects ALL past + future
 * OpEx rows tagged with that vendor (via the AI classifier "(Rule: <name>)"
 * tag in JE memos). Single source of truth: vat_vendor_rules.
 *
 * Props:
 *   open                 — boolean
 *   vendorName           — string, the rule name extracted from JE memo
 *   currentTreatment     — current treatment string ('' if no rule yet)
 *   onClose()            — close handler
 *   onSaved(treatment)   — called after a successful save; caller refreshes data
 */
export default function VATTreatmentEditor({
  open,
  vendorName,        // editable when isNewRule=true; locked otherwise
  currentTreatment,
  isNewRule = false, // true when creating a rule from an unclassified row
  onClose,
  onSaved,
}) {
  const [vendor, setVendor] = useState(vendorName || '');
  const [treatment, setTreatment] = useState(currentTreatment || 'unknown');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // What this vendor name actually binds to. Fetched before saving so the accountant sees the
  // blast radius up front — this modal used to save a rule that silently affected NOTHING.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  // Reset local state when modal re-opens for a different vendor.
  React.useEffect(() => {
    if (open) {
      setVendor(vendorName || '');
      setTreatment(currentTreatment || 'unknown');
      setNotes('');
      setError(null);
      setPreview(null);
    }
  }, [open, vendorName, currentTreatment]);

  // Debounced preview — the vendor name is editable, so re-check as it is typed.
  React.useEffect(() => {
    if (!open) return undefined;
    const name = (vendor || '').trim();
    // A cancelled earlier preview does not clear `previewing` (DT-910 sweep), so clear it here too
    if (!name) { setPreview(null); setPreviewing(false); return undefined; }
    let cancelled = false;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/accounting/vat-preview/vendor-rules/preview', {
          params: { vendor: name },
        });
        if (!cancelled) setPreview(data?.data || null);
      } catch (e) {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, vendor]);

  // The two treatments that actually move the ledger. Everything else is return-only.
  const RATE = { uk_standard_20: 20 / 120, uk_reduced_5: 5 / 105 };
  const estVat = preview && RATE[treatment] ? (preview.gross_native * RATE[treatment]) : 0;

  const handleSave = async () => {
    const cleanVendor = (vendor || '').trim();
    if (!cleanVendor) {
      setError('Vendor name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post('/api/accounting/vat-preview/vendor-rules', {
        vendor: cleanVendor,
        treatment,
        notes,
      });
      onSaved?.(treatment, cleanVendor, data?.data || null);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNewRule ? 'Set VAT rule for this vendor' : 'Edit VAT treatment'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isNewRule ? (
            <>
              <TextField
                label="Vendor name (rule key)"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                fullWidth
                size="small"
                helperText="Must match the bank counterparty EXACTLY — it is saved as an anchored pattern, so 'Klaviyo' will NOT match 'KLAVIYO INC. SOFTWARE BOSTON'. The suggested value is taken from the statement; edit only if it is wrong."
              />
              <Alert severity="info" sx={{ py: 0 }}>
                {preview?.uses_existing_rule
                  ? <>A rule already exists for this name — its existing pattern is kept, and saving
                      re-converges the ledger for the transactions it matches.</>
                  : <>Saving creates an EXACT rule — it matches this counterparty name and nothing
                      else (<code>^{(vendor || '').trim().toLowerCase()}$</code>), then books the VAT
                      split into the ledger for the transactions below.</>}
              </Alert>
            </>
          ) : (
            <>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="body2" color="text.secondary">Vendor:</Typography>
                <Typography variant="body1" fontWeight={500}>{vendor || '(none)'}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Saving updates the treatment for ALL past and future transactions matched by this
                vendor's rule, and re-converges the ledger to match — including removing VAT that
                should no longer be claimed.
              </Typography>
            </>
          )}
          <TextField
            select
            label="Treatment"
            value={treatment}
            onChange={(e) => setTreatment(e.target.value)}
            fullWidth
            size="small"
          >
            {TREATMENTS.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Why this treatment — e.g. 'Non-UK B2B service, accountant simplification'"
          />
          {/* Blast radius, before the write. This modal previously saved a rule that bound to
              nothing at all — showing the matched transaction set is what makes that visible. */}
          {previewing && <Typography variant="caption" color="text.secondary">Checking what this matches…</Typography>}
          {preview && !previewing && (
            preview.conflict_rule ? (
              <Alert severity="warning">
                These transactions are already classified by the rule <strong>{preview.conflict_rule}</strong>,
                which outranks a new rule. Saving here would have no effect — set the treatment on
                “{preview.conflict_rule}” instead.
              </Alert>
            ) : preview.txns === 0 ? (
              <Alert severity="warning">
                No bank transactions match this name exactly, so this treatment will not reach the
                ledger or the VAT return. Check the spelling against the counterparty on the statement.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ py: 0 }}>
                Matches <strong>{preview.txns}</strong> transaction{preview.txns === 1 ? '' : 's'}
                {' '}({preview.currencies.join(', ')} {preview.gross_native.toFixed(2)} gross) from{' '}
                {preview.first_seen}.
                {/* For an EXISTING rule most of the VAT is usually already booked, so quoting
                    gross x rate would badly overstate what this save actually moves. Only the
                    brand-new-rule case can honestly promise the full amount. */}
                {!RATE[treatment]
                  ? <> This treatment reclaims no input VAT, so the ledger will not change.</>
                  : preview.uses_existing_rule
                    ? <> These are matched by this vendor's existing rule. Saving re-checks them against
                        the VAT return and books <strong>only the difference</strong> — which may be nothing.</>
                    : <> Will book about <strong>{estVat.toFixed(2)}</strong> of input VAT to 2210 and reduce the expense by the same amount.</>}
              </Alert>
            )
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !(vendor || '').trim() || !!preview?.conflict_rule}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
